import { useLanguage } from "../context/LanguageContext";
import { motion } from "framer-motion";
import { PiggyBank, Clock, Shield, Leaf } from "lucide-react";

const iconMap = {
  piggyBank: PiggyBank,
  clock: Clock,
  shield: Shield,
  leaf: Leaf,
};

export const BenefitsSection = () => {
  const { t } = useLanguage();
  const benefits = t("benefits.items");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section
      id="benefits"
      className="
        section-padding
        bg-[#071423]            /* JOUR : bleu */
        dark:bg-[#141617]       /* NUIT : gris */
        transition-colors duration-300
      "
    >
      <div className="container-width">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <span className="text-[#63C25B] font-semibold text-sm tracking-wider mb-3 block">
            {t("benefits.sectionTag")}
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            {t("benefits.title")}
          </h2>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-4xl mx-auto flex flex-col gap-6"
        >
          {benefits.map((benefit, index) => {
            const Icon = iconMap[benefit.icon];

            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="
                  relative
                  rounded-2xl
                  p-7
                  transition-all duration-300
                  
                  /* JOUR */
                  bg-[#0E1E2F]/80
                  border border-white/10
                  hover:border-[#63C25B]/35

                  /* NUIT */
                  dark:bg-[#1B1E1F]
                  dark:border-white/10
                  dark:hover:border-[#63C25B]/40
                  dark:shadow-[0_14px_40px_rgba(0,0,0,0.35)]
                "
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-[#63C25B]/10 rounded-xl flex items-center justify-center shrink-0">
                    <Icon className="text-[#63C25B]" size={24} />
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">
                      {benefit.title}
                    </h3>

                    <p className="text-slate-300/90 leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </div>

                {/* Inner edge */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/30 dark:ring-black/50" />
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-14"
        >
          <button
            onClick={() =>
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-primary"
            data-testid="benefits-cta"
          >
            {t("hero.cta")}
          </button>
        </motion.div>
      </div>
    </section>
  );
};
